// Fixed skill order + UI labels (CALL-TYPE-TEMPLATES.md UI label ↔ skill_id map).
// Drives the Runtime Monitor grid, the expected-minimum panel, and the actual-performance column.
export const SKILL_ORDER: { id: string; label: string }[] = [
  { id: 'upfront-contract', label: 'Setting Expec.' },
  { id: 'call-prep', label: 'Call Prep' },
  { id: 'personalization', label: 'Personalization' },
  { id: 'problem-development', label: 'Problem Dev' },
  { id: 'moneyball-gap-score', label: 'Gap Identifier' },
  { id: 'aha-moment', label: 'A-Ha Moment' },
  { id: 'objection-handling', label: 'Objection Handling' },
  { id: 'competitive-proactivity', label: 'Competitive Proactivity' },
  { id: 'domain-expertise', label: 'Domain Expertise' },
  { id: 'active-listening', label: 'Active Listening' },
  { id: 'next-steps', label: 'Next Steps' },
];

export const SKILL_LABEL: Record<string, string> = Object.fromEntries(
  SKILL_ORDER.map((s) => [s.id, s.label]),
);

// Per-skill explainer used by the Quick Snapshot (i) tooltips and mirrored, in longer prose, in the
// Getting Started guide (#gs-skills). `measures` = what the skill scores in the call; `purpose` = why
// it matters for the deal. Kept short here so the hover bubble stays readable — the guide has the depth.
export interface SkillInfo {
  measures: string;
  purpose: string;
}
export const SKILL_INFO: Record<string, SkillInfo> = {
  'upfront-contract': {
    measures:
      'Whether the rep set an upfront contract at the start — agreeing the purpose, agenda, and desired outcome, and getting the buyer to buy in.',
    purpose: 'A shared agenda earns permission to steer the call, surfaces hidden agendas, and keeps both sides from wasting the meeting.',
  },
  'call-prep': {
    measures:
      'On a follow-up call, whether the rep recapped prior touchpoints (past meetings, the buyer’s pain, what was agreed, open questions) and asked the buyer to confirm before today’s agenda.',
    purpose: 'Continuity: it proves the rep prepared, re-anchors the buyer, and avoids re-treading ground already covered.',
  },
  personalization: {
    measures:
      'Whether the rep tailored the conversation to this buyer’s real world — their role, context, and situation — instead of running a generic pitch (behaviour in the call, not background research).',
    purpose: 'Relevance builds trust and shows the solution fits the buyer’s reality, not a template.',
  },
  'problem-development': {
    measures:
      'Whether the rep developed the buyer’s stated need into the underlying business driver — the real reason that justifies change — rather than accepting a shallow need and pitching.',
    purpose: 'It reveals whether there is a genuine, fundable problem worth solving before any solution is offered.',
  },
  'moneyball-gap-score': {
    measures:
      'The quality of gap selling — establishing a clear, buyer-validated gap between where the buyer is today and where they want to be, tied to impact and urgency.',
    purpose: 'Quantifying the cost of inaction builds the business case for change instead of a feature pitch.',
  },
  'aha-moment': {
    measures:
      'Whether the buyer had a genuine realization — a shift in understanding, urgency, or how they see their problem — not just polite agreement.',
    purpose: 'Real momentum comes from the buyer seeing something new for themselves, not from the seller asserting it.',
  },
  'objection-handling': {
    measures:
      'How well the rep handled pushback and concerns — budget, timing, authority, trust, competitor, or product — by acknowledging, exploring, and mitigating them, ideally proactively.',
    purpose: 'Unresolved objections quietly stall deals; strong handling keeps momentum and builds trust.',
  },
  'competitive-proactivity': {
    measures:
      'Whether the rep proactively surfaced and shaped the buyer’s alternatives — rivals, the incumbent, build-vs-buy, and the “do nothing” option — before the buyer raised them, and shaped the decision criteria.',
    purpose: 'Deals are won or lost against alternatives; getting ahead of them protects the deal.',
  },
  'domain-expertise': {
    measures:
      'The rep’s command of the buyer’s world — market and industry trends, the customer’s own challenges, and fluent, correct use of the buyer’s terminology with no buyer confusion.',
    purpose: 'Credibility: buyers trust and buy from someone who clearly understands their business.',
  },
  'active-listening': {
    measures:
      'Whether the rep proved they heard the buyer — reflecting and paraphrasing the buyer’s own words, reacting in context, giving airtime and silence, and catching emotion or the unspoken.',
    purpose: 'Listening earns a “that’s right”, surfaces the truth, and builds the trust that moves deals.',
  },
  'next-steps': {
    measures:
      'The close: a wrap-up first — recapping and inviting the buyer to confirm — then a clear, mutual, specific, time-bound next step with the right stakeholders.',
    purpose: 'It converts a good conversation into forward motion instead of a vague “I’ll follow up.”',
  },
};
export function skillInfoById(id: string): SkillInfo | undefined {
  return SKILL_INFO[id];
}

// UI-only display overrides for skill names that arrive as data (skill_name from the scorecard,
// aggregator, or trend/compare APIs). The canonical name is preserved in the scoring contract,
// the getting-started guide, and other resources; this only shortens what the app UI renders.
// Wrap any user-visible skill_name / node.name with displaySkillName() at the render site.
const UI_SKILL_NAME_OVERRIDES: Record<string, string> = {
  'Moneyball Gap Identifier': 'Gap Identifier',
};
export function displaySkillName(name: string): string {
  return UI_SKILL_NAME_OVERRIDES[name] ?? name;
}

// skill_id → UI label. The Trends API keys everything by contract skill_id (e.g. 'upfront-contract'),
// whereas displaySkillName maps skill NAMES; this bridges ids to the tight bar labels in SKILL_ORDER.
const LABEL_BY_ID: Record<string, string> = Object.fromEntries(SKILL_ORDER.map((s) => [s.id, s.label]));
export function skillLabelById(id: string): string {
  return LABEL_BY_ID[id] ?? id;
}

// Apply the same UI-only overrides to skill names embedded in a FREE-TEXT string — e.g. the coaching
// action items ("Where do we go from here?") build a sentence out of raw skill_names server-side, so a
// per-field displaySkillName() doesn't reach them. The canonical name still lives in the data/contract;
// this only rewrites what the UI shows.
export function displaySkillNamesInText(text: string): string {
  let out = text;
  for (const [canonical, display] of Object.entries(UI_SKILL_NAME_OVERRIDES)) {
    out = out.split(canonical).join(display);
  }
  return out;
}

/** The skill_ids the UI has a fixed bar/row for. A scored skill whose id isn't here can't be placed
 *  on the performance bars — the watchdog (improvement.ts) surfaces that rather than silently drop it. */
export const SKILL_IDS: ReadonlySet<string> = new Set(SKILL_ORDER.map((s) => s.id));
